import React, { useState, useEffect, useRef, useContext } from "react";
import { useHistory } from "react-router-dom";
import { Formik, Field, Form } from "formik";
import * as Yup from "yup";

import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import Select from "@material-ui/core/Select";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import MenuItem from "@material-ui/core/MenuItem";
import { makeStyles } from "@material-ui/core";

import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Autocomplete, {
  createFilterOptions,
} from "@material-ui/lab/Autocomplete";
import CircularProgress from "@material-ui/core/CircularProgress";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import ButtonWithSpinner from "../ButtonWithSpinner";
import toastError from "../../errors/toastError";
import useQueues from "../../hooks/useQueues";
import { TicketsContext } from "../../context/Tickets/TicketsContextIndexedDB";
import { toast } from "react-toastify";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles((theme) => ({
  maxWidth: {
    width: "100%",
  },
}));

const filterOptions = createFilterOptions({
  trim: true,
});

const TransferSchema = Yup.object().shape({
  selectedUser: Yup.object().nullable().required("Requerido"),
  selectedQueue: Yup.string().optional().nullable(),
  transferComment: Yup.string().max(500, "El comentario no puede exceder 500 caracteres"),
});

const TransferTicketModalCustom = ({ modalOpen, onClose, ticketid, reloadTickets }) => {
  const history = useHistory();
  const [options, setOptions] = useState([]);
  const [queues, setQueues] = useState([]);
  const [allQueues, setAllQueues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedQueue, setSelectedQueue] = useState("");
  const classes = useStyles();
  const { findAll: findAllQueues } = useQueues();
  const isMounted = useRef(true);
  const [queueDisabled, setQueueDisabled] = useState(true);
  const { setCurrentTicket, removeTicketFromList } = useContext(TicketsContext);
  const { user } = useContext(AuthContext);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (isMounted.current) {
      const loadQueues = async () => {
        try {
          const list = await findAllQueues();
          setAllQueues(Array.isArray(list) ? list : []);
          setQueues(Array.isArray(list) ? list : []);
        } catch (error) {
          console.error("Error al cargar las colas:", error);
          setAllQueues([]);
          setQueues([]);
        }
      };
      loadQueues();
    }
  }, []);

  useEffect(() => {
    if (!modalOpen || searchParam.length < 3) {
      setLoading(false);
      return;
    }
    const delayDebounceFn = setTimeout(() => {
      setLoading(true);
      const fetchUsers = async () => {
        try {
          const { data } = await api.get("/users/", {
            params: { searchParam },
          });
          setOptions(data.users);
          setLoading(false);
        } catch (err) {
          setLoading(false);
          toastError(err);
        }
      };

      fetchUsers();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, modalOpen]);

  const handleClose = () => {
    onClose();
    setSearchParam("");
    setSelectedUser(null);
  };

  const handleSaveTicket = async (values) => {
    setLoading(true);
    try {
      // Asegurarse de que tanto userId como queueId estén definidos
      if (!values.selectedUser || !values.selectedUser.id) {
        toastError("Debe seleccionar un usuario para transferir el ticket");
        setLoading(false);
        return;
      }
            
      const response = await api.put(`/tickets/${ticketid}`, {
        userId: values.selectedUser.id,
        queueId: values.selectedQueue === '' ? null : values.selectedQueue,
        status: "open",
        unreadMessages: 1,
        transferComment: values.transferComment,
      });

      // Crear notificación para el usuario que recibe el ticket
      try {
        await api.post("/announcements", {
          title: "Nuevo ticket transferido",
          text: `Se te ha asignado un nuevo ticket, ${response.data.contact.name}`,
          status: true,
          priority: 1,
          type: "transfer_notification",
          userId: values.selectedUser.id,
          ticketId: ticketid,
          companyId: values.selectedUser.companyId
        });
      } catch (notificationErr) {
        console.error("Error al crear notificación de transferencia:", notificationErr);
        // No interrumpir el flujo si falla la notificación
      }

      setLoading(false);
      handleClose();
      
      // Limpiar el currentTicket ya que el ticket ha sido transferido
      setCurrentTicket({ id: null, code: null });
      removeTicketFromList(ticketid);
      
      // Redirigir a la lista de tickets
      history.push("/tickets");
      
      // Mostrar mensaje de éxito
      toast.success("Ticket transferido exitosamente");
      
      if (typeof reloadTickets === "function") {
        reloadTickets();
      }
    } catch (err) {
      console.error("Error durante la transferencia del ticket:", err);
      setLoading(false);
      
      // Proporcionar mensajes de error más descriptivos
      if (err.response && err.response.status === 400) {
        toastError(new Error("Error en la transferencia: datos incompletos o inválidos"));
      } else if (err.response && err.response.status === 404) {
        toastError(new Error("El ticket o usuario seleccionado no existe"));
      } else if (err.message && err.message.includes("Network Error")) {
        toastError(new Error("Error de conexión. Verifique su conexión a internet e intente nuevamente"));
      } else {
        toastError(err);
      }
    }
  };

  return (
    <Dialog open={modalOpen} onClose={handleClose} maxWidth="lg" scroll="paper">
      <Formik
        initialValues={{
          selectedUser: null,
          selectedQueue: "",
          transferComment: "",
        }}
        validationSchema={TransferSchema}
        onSubmit={(values, actions) => {
          handleSaveTicket(values);
          actions.setSubmitting(false);
        }}
      >
        {({ touched, errors, isSubmitting, setFieldValue }) => (
          <Form>
            <DialogTitle id="form-dialog-title">
              {i18n.t("transferTicketModal.title")}
            </DialogTitle>
            <DialogContent dividers>
              <Autocomplete
                style={{ width: 300, marginBottom: 20 }}
                getOptionLabel={(option) => `${option.name}`}
                onChange={(e, newValue) => {
                  setFieldValue("selectedUser", newValue);
                  
                  // Si se selecciona un usuario, filtrar las colas disponibles
                  if (newValue) {
                    // Verificar si el usuario tiene colas asignadas
                    console.log("newValue:", newValue);
                    const userQueues = Array.isArray(newValue.UserQueues) && newValue.UserQueues.length > 0 
                      ? newValue.UserQueues.map(uq => uq.Queues)
                      : allQueues;
                    
                    setQueues(userQueues);
                    
                    // Si el usuario solo tiene una cola, seleccionarla automáticamente
                    if (userQueues.length === 1) {
                      setSelectedQueue(userQueues[0].id);
                      setFieldValue("selectedQueue", userQueues[0].id);
                    } else {
                      // Limpiar la selección de cola si hay múltiples opciones
                      setFieldValue("selectedQueue", "");
                      setSelectedQueue("");
                    }
                  } else {
                    // Si no hay usuario seleccionado, mostrar todas las colas pero deshabilitar el selector
                    setQueues(allQueues);
                    setFieldValue("selectedQueue", "");
                    setSelectedQueue("");
                  }
                  
                  // Habilitar/deshabilitar el selector de colas según si hay usuario seleccionado
                  setQueueDisabled(!newValue);
                }}
                options={options}
                filterOptions={filterOptions}
                autoHighlight
                noOptionsText={i18n.t("transferTicketModal.noOptions")}
                loading={loading}
                renderInput={(params) => (
                  <Field
                    as={TextField}
                    {...params}
                    name="selectedUser"
                    label={i18n.t("transferTicketModal.fieldLabel")}
                    variant="outlined"
                    autoFocus
                    onChange={(e) => setSearchParam(e.target.value)}
                    error={touched.selectedUser && Boolean(errors.selectedUser)}
                    helperText={touched.selectedUser && errors.selectedUser}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <React.Fragment>
                          {loading ? (
                            <CircularProgress color="inherit" size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </React.Fragment>
                      ),
                    }}
                  />
                )}
              />
              <FormControl
                variant="outlined"
                className={classes.maxWidth}
                error={touched.selectedQueue && Boolean(errors.selectedQueue)}
              >
                <InputLabel>
                  {i18n.t("transferTicketModal.fieldQueueLabel")}
                </InputLabel>
                <Select
                  as={Select}
                  name="selectedQueue"
                  value={selectedQueue}
                  onChange={(e) => {
                    setSelectedQueue(e.target.value);
                    setFieldValue("selectedQueue", e.target.value);
                  }}
                  label={i18n.t("transferTicketModal.fieldQueuePlaceholder")}
                  disabled={queueDisabled}
                >
                  {Array.isArray(queues) && queues.map((queue) => (
                    <MenuItem key={queue.id} value={queue.id}>
                      {queue.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              <Field
                as={TextField}
                name="transferComment"
                label="Comentario de transferencia"
                variant="outlined"
                multiline
                rows={3}
                fullWidth
                style={{ marginTop: 20 }}
                placeholder="Agrega un comentario sobre la transferencia..."
                error={touched.transferComment && Boolean(errors.transferComment)}
                helperText={touched.transferComment && errors.transferComment}
                inputProps={{ maxLength: 500 }}
              />
            </DialogContent>
            <DialogActions>
              <Button
                onClick={handleClose}
                color="secondary"
                disabled={isSubmitting}
                variant="outlined"
              >
                {i18n.t("transferTicketModal.buttons.cancel")}
              </Button>
              <ButtonWithSpinner
                variant="contained"
                type="submit"
                color="primary"
                loading={isSubmitting}
              >
                {i18n.t("transferTicketModal.buttons.ok")}
              </ButtonWithSpinner>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default TransferTicketModalCustom;
